import { create } from 'zustand';
import { isBelowMin } from '#shared/core/version';

/**
 * Holds for the update prompt. While any hold is active (this phone keeps the book of a live
 * game, a drill is being recorded), "New version on the bench" waits and the Me tab shows a dot.
 *
 *   useEffect(() => holdUpdates('games:keeper'), []);   // returns the release function
 *
 * Before activating a new worker, the prompt closes live streams registered here and flushes
 * the outbox, so no fetch stays in flight (an open EventSource blocks activation).
 */
interface UpdateState {
  holds: Set<string>;
  closers: Set<() => void>;
  /** A new worker is installed and waiting for the person to say go (set by the update prompt). */
  waiting: boolean;
}

export const useUpdateState = create<UpdateState>(() => ({ holds: new Set(), closers: new Set(), waiting: false }));

export function setUpdateWaiting(waiting: boolean): void {
  if (useUpdateState.getState().waiting !== waiting) useUpdateState.setState({ waiting });
}

export function holdUpdates(reason: string): () => void {
  useUpdateState.setState((s) => ({ holds: new Set(s.holds).add(reason) }));
  return () =>
    useUpdateState.setState((s) => {
      const holds = new Set(s.holds);
      holds.delete(reason);
      return { holds };
    });
}

/** Register a function that closes an EventSource (SSE). Returns the unregister. */
export function registerLiveStream(close: () => void): () => void {
  useUpdateState.getState().closers.add(close);
  return () => useUpdateState.getState().closers.delete(close);
}

export function closeLiveStreams(): void {
  for (const c of useUpdateState.getState().closers) {
    try {
      c();
    } catch {
      // a stream that is already closed is fine
    }
  }
}

export const useUpdateHeld = (): boolean => useUpdateState((s) => s.holds.size > 0);

/** True when an update is waiting but a hold keeps the prompt away: the Me tab shows a dot. */
export const useUpdateDot = (): boolean => useUpdateState((s) => s.waiting && s.holds.size > 0);

/**
 * True when this build is older than the server's `config.minClient`. The update prompt turns
 * blocking on it, the same as on a 426 from any call. Ignores the +build stamp.
 */
export function buildBelowMin(buildVersion: string, minClient: string | null | undefined): boolean {
  return !!minClient && isBelowMin(buildVersion, minClient);
}

/**
 * What a check for a new worker found: one is installed and waiting, one is still downloading
 * after the long wait, or the browser has none.
 */
export type WorkerCheck = 'waiting' | 'downloading' | 'none';

/** What `applyUpdate` needs from the page, so the order of steps can be tested without a browser. */
export interface ApplyDeps {
  closeStreams(): void;
  /** Push the outbox up. May hang on a bad connection; the rows stay safe either way. */
  flush(): Promise<void>;
  /** vite-plugin-pwa's updateServiceWorker(true): tells the waiting worker to take over, reloads when it has. */
  activate(): Promise<void>;
  reload(): void;
  /**
   * Ask the browser to look for a new worker now. Resolves 'waiting' when one is installed, and
   * 'downloading' when one is still installing after the long wait. Calls `onDownloading` when it
   * starts waiting on an install, so the card can say so.
   */
  checkForWaiting(onDownloading: () => void): Promise<WorkerCheck>;
  /** Last resort for a forced update: drop the old worker and its precache so a reload hits the network. */
  unregister?(): Promise<void>;
  /** The card shows "Downloading the update". */
  onDownloading?(): void;
  sleep(ms: number): Promise<void>;
}

export const UPDATE_FLUSH_MS = 4000;
export const UPDATE_FALLBACK_RELOAD_MS = 3000;
/** How long to look for a worker to show up at all after `reg.update()`. */
export const UPDATE_DISCOVER_MS = 6000;
/** How long an installing worker may take (about 1.6 MB of precache on weak signal). */
export const UPDATE_INSTALL_MS = 90_000;

export type ApplyOutcome = 'activated' | 'reloaded' | 'downloading';

/**
 * The tap on "Sub it in" or "Update now". In order:
 *   1. close the live streams (an open EventSource keeps a fetch in flight and blocks a new
 *      worker from activating);
 *   2. give the outbox a few seconds to empty (it keeps what it could not send);
 *   3. if a worker is waiting, activate it. If none is (the 426 path on a phone that never
 *      noticed the new build), ask for one. A worker that is still installing is waited for, with
 *      the card saying so, because a reload before it finishes boots the OLD build: the old worker
 *      answers every navigation with its own precached index.html;
 *   4. if the page has not reloaded by itself a moment after activation, reload it;
 *   5. with no worker anywhere, reload. On a forced update the old worker is unregistered first
 *      (with its caches), so the reload cannot be answered from the stale precache.
 *
 * 'downloading' means an install is still running after the long wait: nothing was reloaded, the
 * caller says to keep the app open and try again.
 */
export async function applyUpdate(
  deps: ApplyDeps,
  workerWaiting: boolean,
  opts: { forced?: boolean } = {},
): Promise<ApplyOutcome> {
  deps.closeStreams();
  await Promise.race([deps.flush().catch(() => {}), deps.sleep(UPDATE_FLUSH_MS)]);
  const check: WorkerCheck = workerWaiting
    ? 'waiting'
    : await deps.checkForWaiting(() => deps.onDownloading?.()).catch((): WorkerCheck => 'none');
  if (check === 'downloading') return 'downloading';
  if (check === 'none') {
    if (opts.forced) await deps.unregister?.().catch(() => {});
    deps.reload();
    return 'reloaded';
  }
  await deps.activate().catch(() => {});
  await deps.sleep(UPDATE_FALLBACK_RELOAD_MS);
  deps.reload();
  return 'activated';
}

interface WorkerLike {
  state: string;
}

/** Polls an installing worker until it is installed (or redundant, or the time is up). */
export async function waitForInstall(
  worker: WorkerLike,
  ms: number,
  sleep: (ms: number) => Promise<void> = (n) => new Promise((r) => setTimeout(r, n)),
): Promise<void> {
  const step = 250;
  for (let waited = 0; waited <= ms; waited += step) {
    if (worker.state !== 'installing') return;
    await sleep(step);
  }
}

/**
 * After `reg.update()`: polls `reg.waiting` for up to `ms` (a new worker takes a moment to show
 * up). If one is installing by then, waits for it for up to `installMs` and calls `onDownloading`
 * first. Resolves 'waiting' when a worker is waiting, 'downloading' when an install is still
 * running at the end, 'none' when the browser found nothing.
 */
export async function waitForWaitingWorker(
  reg: { update(): Promise<unknown>; waiting: unknown; installing?: WorkerLike | null },
  ms: number,
  sleep: (ms: number) => Promise<void> = (n) => new Promise((r) => setTimeout(r, n)),
  opts: { installMs?: number; onDownloading?: () => void } = {},
): Promise<WorkerCheck> {
  await reg.update().catch(() => {});
  const step = 250;
  for (let waited = 0; waited <= ms; waited += step) {
    if (reg.waiting) return 'waiting';
    if (reg.installing) break;
    await sleep(step);
  }
  if (reg.waiting) return 'waiting';
  const installing = reg.installing;
  if (!installing) return 'none';
  opts.onDownloading?.();
  await waitForInstall(installing, opts.installMs ?? UPDATE_INSTALL_MS, sleep);
  if (reg.waiting) return 'waiting';
  if (installing.state === 'installing') return 'downloading';
  // installed, activating or activated: a new worker is there. redundant: the install failed.
  return installing.state === 'redundant' ? 'none' : 'waiting';
}
